import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-lg text-base font-medium font-inter transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-95 relative overflow-hidden",
  {
    variants: {
      variant: {
        primary: "text-white hover:shadow-lg",
        secondary: "bg-vyl-button text-vyl-dark hover:bg-opacity-80",
        outline: "border border-vyl-dark text-vyl-dark hover:bg-vyl-dark hover:text-white",
        ghost: "text-vyl-dark hover:bg-vyl-card",
      },
      size: {
        sm: "h-8 px-4 text-xs min-w-[80px]",
        md: "h-10 px-6 text-sm min-w-[100px]",
        lg: "h-12 px-8 text-base min-w-[120px]",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  children: React.ReactNode;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, children, loading, disabled, ...props }, ref) => {
    const isPrimary = variant === 'primary';
    
    return (
      <button
        className={buttonVariants({ variant, size, className })}
        ref={ref}
        disabled={disabled || loading}
        style={{
          background: isPrimary 
            ? 'linear-gradient(to bottom, #403634 0%, #1E1E1E 100%)' 
            : undefined
        }}
        {...props}
      >
        {/* Hover overlay for primary variant */}
        {isPrimary && !disabled && (
          <div 
            className="absolute inset-0 opacity-0 hover:opacity-10 transition-opacity duration-200"
            style={{
              background: 'linear-gradient(to bottom, #ffffff 0%, #ffffff 100%)'
            }}
          />
        )}
        
        {/* Content wrapper */}
        <div className="relative z-10 flex items-center">
          {loading && (
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          )}
          {children}
        </div>
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;